/**
 * Theme application (Phase-2 A7). The design system already ships light/dark CSS
 * variables (the `.dark` class on <html>), and the user-preferences schema
 * already has a `theme` field — this wires them up. Theme is cached in
 * localStorage for instant, flash-free application on boot and synced from the
 * server preference (/me) when available.
 *
 * How to disable / undo: remove the theme selector in settings + the initTheme()
 * call in main.tsx. No backend change (the `theme` preference already exists).
 */
export type Theme = 'light' | 'dark' | 'system';

const KEY = 'dgm_theme';

export function getStoredTheme(): Theme {
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

export function setStoredTheme(theme: Theme): void {
  localStorage.setItem(KEY, theme);
}

/** Resolve `system` against the OS preference and toggle the `dark` class. */
export function applyTheme(theme: Theme): void {
  const dark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
}

/** Apply the stored theme on boot (call before first render to avoid flash). */
export function initTheme(): void {
  applyTheme(getStoredTheme());
}
